import { useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined'
import { useTranslation } from 'react-i18next'
import { billingService, type CurrentStanding } from '@/services/billingService'
import { askableQuestions } from './askable'
import { respondentMinutes } from './estimate'
import { Group, Row } from './desk'
import type { StudyDraft } from './types'

/**
 * Last look before the link goes out, at the end of the last step: what is
 * ready, and what the study will take from the respondent package. Only the
 * package is fetched — the rest is the draft. A slot is spent when a
 * respondent starts a session (`studies_app/public_views.py`), so the copy
 * says "up to", never "only completed ones". Below it, the AI Act line (Z-90).
 */
export function LaunchChecklist({ draft }: { draft: StudyDraft }) {
  const { t } = useTranslation('dashboard')
  const c = useTheme().affexy.colors
  const [standing, setStanding] = useState<CurrentStanding | null>(null)

  useEffect(() => {
    let cancelled = false
    billingService
      .getPlans()
      .then((data) => !cancelled && setStanding(data.current))
      .catch(() => !cancelled && setStanding(null))
    return () => {
      cancelled = true
    }
  }, [])

  const readyStimuli = draft.stimuli.filter((s) => s.status === 'ready').length
  const questions = askableQuestions(draft.questions, draft.demographics).length
  const minutes = respondentMinutes(draft)
  const limit = Number(draft.responseLimit) || Number(draft.targetRespondents) || 0
  const pool = standing?.respondents
  const checks: { ok: boolean; label: string; value?: string }[] = [
    { ok: draft.name.trim().length > 0, label: t('wizard.desk.launch.name'), value: draft.name.trim() || undefined },
    {
      ok: readyStimuli > 0 && readyStimuli === draft.stimuli.length,
      label: t('wizard.desk.launch.stimuli', { count: draft.stimuli.length }),
      value: draft.stimuli.length > readyStimuli ? t('wizard.desk.launch.uploading') : undefined,
    },
    {
      ok: questions > 0,
      label: questions > 0 ? t('wizard.desk.launch.questions', { count: questions }) : t('wizard.desk.launch.noQuestions'),
      value: minutes ? t('wizard.desk.rail.minutes', { count: minutes }) : undefined,
    },
  ]

  return (
    <>
      <Group title={t('wizard.desk.launch.title')} flush>
        {checks.map((ch) => (
          <Row key={ch.label}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
              {ch.ok ? (
                <CheckCircleRoundedIcon sx={{ fontSize: 20, color: '#1F8A5B', flexShrink: 0 }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: c.ink32, flexShrink: 0 }} />
              )}
              <Typography sx={{ fontSize: 14.5, color: ch.ok ? c.ink : c.ink56, flexShrink: 0 }}>{ch.label}</Typography>
              <Typography
                sx={{ fontSize: 14, color: c.ink56, ml: 'auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
              >
                {ch.value ?? ''}
              </Typography>
            </Box>
          </Row>
        ))}
        {pool && pool.limit != null && pool.remaining != null && (
          <Row>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
              <Typography sx={{ fontSize: 14.5, flex: 1 }}>{t('wizard.desk.launch.package')}</Typography>
              <Typography sx={{ fontSize: 14.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {t('wizard.desk.launch.packageLeft', { remaining: pool.remaining, limit: pool.limit })}
              </Typography>
            </Box>
            <Box sx={{ height: 6, borderRadius: 9, bgcolor: '#EEEEF3', overflow: 'hidden', my: 1.25 }}>
              <Box sx={{ width: `${Math.min(100, (pool.used / Math.max(1, pool.limit)) * 100)}%`, height: '100%', bgcolor: c.coral, borderRadius: 9 }} />
            </Box>
            {limit > 0 && (
              <Typography sx={{ fontSize: 13, color: limit > pool.remaining ? c.danger : c.ink56, lineHeight: 1.45 }}>
                {limit > pool.remaining
                  ? t('wizard.desk.launch.packageShort', { limit, remaining: pool.remaining })
                  : t('wizard.desk.launch.packageUse', { limit })}
              </Typography>
            )}
          </Row>
        )}
      </Group>

      {/* AI Act, art. 5(1)(f) (Z-90) — the last thing read before launching. */}
      <Box sx={{ display: 'flex', gap: 1.25, mt: 2, px: 0.25, alignItems: 'flex-start' }}>
        <GavelOutlinedIcon sx={{ fontSize: 16, color: c.ink48, mt: '2px', flexShrink: 0 }} />
        <Typography sx={{ fontSize: 12.5, color: c.ink56, lineHeight: 1.55 }}>{t('wizard.distribution.aiActNotice')}</Typography>
      </Box>
    </>
  )
}
